import { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { fetchBacktests, fetchBacktestSummary } from '../backtestsSlice';

/**
 * Custom hook for polling backtests when there are pending/processing requests
 * @param {Array} backtests - Array of backtest objects
 * @param {Object} filters - Current filters applied to backtests
 * @param {Object} options - Polling configuration options
 * @returns {Object} - Polling status and controls
 */
export const useBacktestPolling = (backtests, filters, options = {}) => {
  const dispatch = useDispatch();
  const intervalRef = useRef(null);
  const lastPollRef = useRef(0);
  
  const {
    baseInterval = 10000, // 10 seconds base interval
    activeInterval = 5000, // 5 seconds for active backtests
    maxInterval = 60000, // 1 minute max interval
    onBacktestComplete = null,
    onBacktestFailed = null,
    enabled = true
  } = options;

  // Track previous backtest statuses to detect changes
  const previousStatusesRef = useRef(new Map());

  // Check if there are any active backtests (pending or processing)
  const hasActiveBacktests = useCallback(() => {
    return backtests.some(bt => 
      bt.status === 'pending' || 
      bt.status === 'processing'
    );
  }, [backtests]);

  // Detect status changes and trigger callbacks
  const detectStatusChanges = useCallback(() => {
    const currentStatuses = new Map();
    const changedBacktests = [];

    backtests.forEach(backtest => {
      const { requestId, status } = backtest;
      const previousStatus = previousStatusesRef.current.get(requestId);
      
      currentStatuses.set(requestId, status);
      
      // Detect status changes
      if (previousStatus && previousStatus !== status) {
        changedBacktests.push({
          backtest,
          previousStatus,
          currentStatus: status
        });
      }
    });

    // Update previous statuses
    previousStatusesRef.current = currentStatuses;

    // Handle status change callbacks
    changedBacktests.forEach(({ backtest, previousStatus, currentStatus }) => {
      if (previousStatus === 'processing' && currentStatus === 'completed') {
        onBacktestComplete?.(backtest);
      } else if (previousStatus === 'processing' && currentStatus === 'failed') {
        onBacktestFailed?.(backtest);
      }
    });

    return changedBacktests;
  }, [backtests, onBacktestComplete, onBacktestFailed]);

  // Determine appropriate polling interval
  const getPollingInterval = useCallback(() => {
    const activeCount = backtests.filter(bt => 
      bt.status === 'pending' || bt.status === 'processing'
    ).length;

    if (activeCount === 0) {
      return null; // No polling needed
    }
    if (activeCount >= 3) {
      return activeInterval; // Frequent polling for multiple active
    }
    if (activeCount >= 1) {
      return baseInterval; // Normal polling for single active
    }
    
    return maxInterval; // Fallback
  }, [backtests, activeInterval, baseInterval, maxInterval]);

  // Polling function
  const pollBacktests = useCallback(async () => {
    const now = Date.now();
    
    // Prevent too frequent polling
    if (now - lastPollRef.current < 3000) {
      return;
    }
    
    lastPollRef.current = now;
    
    try {
      // Fetch updated backtests and summary
      await Promise.all([
        dispatch(fetchBacktests({ ...filters, lastEvaluatedKey: null, append: false })),
        dispatch(fetchBacktestSummary())
      ]);
    } catch (error) {
      // Silently handle polling errors
    }
  }, [dispatch, filters]);

  // Start/stop polling based on active backtests
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const interval = getPollingInterval();
    const shouldPoll = hasActiveBacktests() && interval;

    if (shouldPoll) {
      // Clear existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Start new polling interval
      intervalRef.current = setInterval(pollBacktests, interval);
    } else {
      // Stop polling
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    // Cleanup on unmount or dependency change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, hasActiveBacktests, getPollingInterval, pollBacktests]);

  // Detect status changes whenever backtests update
  useEffect(() => {
    detectStatusChanges();
  }, [detectStatusChanges]);

  // Manual refresh function
  const refreshNow = useCallback(() => {
    pollBacktests();
  }, [pollBacktests]);

  // Stop polling function
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  return {
    isPolling: !!intervalRef.current,
    hasActiveBacktests: hasActiveBacktests(),
    activeCount: backtests.filter(bt => 
      bt.status === 'pending' || bt.status === 'processing'
    ).length,
    refreshNow,
    stopPolling
  };
};

export default useBacktestPolling;
